import { preserveNodeMeasurements } from './Components/preserveNodeMeasurements';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Background, Connection, Controls, Edge, MarkerType, Node, ReactFlow, ReactFlowProvider, useEdgesState, useNodesState } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import React, { type CSSProperties, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { AddCircle, Code, Extension, Groups, Inventory2, Refresh, RocketLaunch, VpnKey } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Paper, Typography, alpha, useTheme } from '@mui/material';

import { useApiClient, useApiClients, useApiEnvironments, useApiKeys, useIsMobile, usePersistedSetting, useReactFlowAutoFit, useToastService } from '@@common';
import { ConfirmDialog, ViewTitleBar, useNotificationSubscription } from '@@components';

import {
	ApiKeyModel,
	ClientModel,
	ComponentModel,
	ContextClient,
	CreateEnvironmentCommandResult,
	EnvironmentApiKeyModel,
	EnvironmentClient,
	EnvironmentClientModel,
	EnvironmentModel,
	EnvironmentTransition,
} from '../../api/api';
import Spinner from '../../common/components/Spinner';
import { usePresence } from '../../components/Presence/usePresence';
import { PresenceContext } from '../../components/Presence/PresenceContext';
import { PresenceIndicator, viewersForItem } from '../../components/Presence/PresenceIndicator';
import { getEnvironmentPresenceLocation } from './EnvironmentPresenceLocation';
import EmbedsPanel from './Components/EmbedsPanel';
import { contextDesignerQueryKeys } from '../ContextDesigner/ContextDesignerQueryKeys';
import ResponsivePanelContainer from '../../components/Panels/ResponsivePanelContainer';
import { usePortalTheme } from '../../themes/PortalThemeProvider';
import { flowThemeTokens } from '../../themes/flowTokens';
import ServiceConnectionMetadataProvider from '../ServiceConnections/Contexts/ServiceConnectionMetadataContext';
import {
	API_KEYS_PANEL_DEFAULT_WIDTH,
	ApiKeyDetailsPanel,
	ApiKeysPanel,
	CLIENTS_PANEL_DEFAULT_WIDTH,
	COMPONENTS_PANEL_DEFAULT_WIDTH,
	CONTAINER_IMAGES_PANEL_DEFAULT_WIDTH,
	ClientDetailsPanel,
	ClientsPanel,
	ComponentDetailsPanel,
	ComponentsPanel,
	ContainerImageDetailsPanel,
	ContainerImagesPanel,
	CreateEnvironmentPlaceholderNode,
	EnvironmentComponentDetailsPanel,
	EnvironmentDetailsPanel,
	EnvironmentNode,
	PromoteDialog,
	PromoteEdge,
	RELEASE_PLANS_PANEL_DEFAULT_WIDTH,
	ReleasePlanDetailsPanel,
	ReleasePlansPanel,
} from './Components';
import { CloudProviderType, getEnvironmentCloudProvider } from './Components/Consts';
import CreateEnvironmentDialog from './CreateEnvironmentDialog';
import EditEnvironmentDialog from './EditEnvironmentDialog';
import './EnvironmentsView.scss';
import useEnvironmentApiKeyActions from './hooks/useEnvironmentApiKeyActions';
import useEnvironmentClientActions from './hooks/useEnvironmentClientActions';

const nodeTypes = {
	environment: EnvironmentNode,
	createEnvironmentPlaceholder: CreateEnvironmentPlaceholderNode,
};

const edgeTypes = {
	promote: PromoteEdge,
};

// Default edge options to ensure arrows are visible
const defaultEdgeOptions = {
	type: 'smoothstep',
	markerEnd: {
		type: MarkerType.ArrowClosed,
	},
};

type EnvironmentGraphLayer = 'components' | 'clients' | 'apiKeys' | 'embeds';
type EnvironmentPanelFamily = 'releasePlans' | 'environment' | 'environmentComponent' | 'containerImages' | 'components' | 'clients' | 'apiKeys' | 'embeds';
type EnvironmentPanelFamilyAction = { type: 'open'; family: EnvironmentPanelFamily } | { type: 'close' };
const ENVIRONMENT_PANELS_TOP = 48;
const DEFAULT_GRAPH_LAYERS: EnvironmentGraphLayer[] = ['components'];
const DEFAULT_NODE_SPACING = 350;
const DEFAULT_NODE_START_X = 100;
const DEFAULT_NODE_Y = 100;
const ENVIRONMENT_GRAPH_INITIALIZED_METADATA_KEY = 'graphInitialized';

function environmentPanelFamilyReducer(_: EnvironmentPanelFamily | null, action: EnvironmentPanelFamilyAction): EnvironmentPanelFamily | null {
	return action.type === 'open' ? action.family : null;
}

function normalizeGraphLayers(value: EnvironmentGraphLayer[] | EnvironmentGraphLayer | undefined): EnvironmentGraphLayer[] {
	if (Array.isArray(value)) {
		const selected = value.find((item) => item === 'components' || item === 'clients' || item === 'apiKeys' || item === 'embeds');
		return selected ? [selected] : DEFAULT_GRAPH_LAYERS;
	}

	return value === 'clients' || value === 'apiKeys' || value === 'components' || value === 'embeds' ? [value] : DEFAULT_GRAPH_LAYERS;
}

function getMetadataNumber(metadata: { [key: string]: any } | undefined, key: string): number | undefined {
	const value = metadata?.[key];
	return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function createEnvironmentTransitionId(sourceId: string, targetId: string) {
	return globalThis.crypto?.randomUUID?.() ?? `${sourceId}-${targetId}-${Date.now()}`;
}

function isEnvironmentGraphInitialized(environment: EnvironmentModel) {
	return (environment.transitions ?? []).length > 0 || environment.metadata?.[ENVIRONMENT_GRAPH_INITIALIZED_METADATA_KEY] === true;
}

function hasInitializedEnvironmentGraph(environments: EnvironmentModel[] | undefined) {
	return environments?.some(isEnvironmentGraphInitialized) ?? false;
}

function makeEnvironmentTransition(sourceId: string, targetId: string, order: number) {
	return {
		id: createEnvironmentTransitionId(sourceId, targetId),
		to: targetId,
		order,
	};
}

function toEnvironmentTransition(transition: Pick<EnvironmentTransition, 'id' | 'to' | 'metadata'> & { order?: number }, index: number): EnvironmentTransition {
	return {
		id: transition.id,
		to: transition.to,
		order: transition.order ?? index,
		metadata: transition.metadata,
	};
}

function getLegacyTransitionUpdates(environments: EnvironmentModel[], excludedTransitionKeys: Set<string> = new Set()) {
	const sortedEnvironments = [...environments].sort((a, b) => (a.order || 0) - (b.order || 0));
	const transitionsBySource = new Map<string, EnvironmentTransition[]>();

	sortedEnvironments.forEach((environment) => transitionsBySource.set(environment.id, []));
	sortedEnvironments.forEach((environment, index) => {
		if (index === 0) {
			return;
		}

		const sourceEnvironment = sortedEnvironments[index - 1];
		const transitionKey = `${sourceEnvironment.id}:${environment.id}`;
		if (excludedTransitionKeys.has(transitionKey)) {
			return;
		}

		transitionsBySource.get(sourceEnvironment.id)?.push(makeEnvironmentTransition(sourceEnvironment.id, environment.id, index - 1));
	});

	return transitionsBySource;
}

// Wrapper component to use the useReactFlowAutoFit hook
const EnvironmentsFlowGraph: React.FC<{
	nodes: Node[];
	edges: Edge[];
	onNodesChange: any;
	onEdgesChange: any;
	onConnect: (connection: Connection) => void;
	onEdgesDelete: (edges: Edge[]) => void;
	onNodeDragStop: (event: React.MouseEvent, node: Node) => void;
	isValidConnection: (connection: Connection | Edge) => boolean;
}> = ({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onEdgesDelete, onNodeDragStop, isValidConnection }) => {
	const theme = useTheme();
	const { themeName } = usePortalTheme();
	const flowTokens = flowThemeTokens[themeName];
	// Auto fit view when nodes are first loaded (fires after nodes are measured)
	useReactFlowAutoFit();
	const canvasColor = flowTokens.canvas;
	const graphVariables = {
		'--environment-graph-surface': canvasColor,
		'--environment-graph-panel': theme.palette.background.paper,
		'--environment-graph-border': theme.palette.divider,
		'--environment-graph-primary': theme.palette.primary.main,
		'--environment-graph-text': theme.palette.text.primary,
	} as CSSProperties;

	return (
		<ReactFlow
			nodes={nodes}
			edges={edges}
			onNodesChange={onNodesChange}
			onEdgesChange={onEdgesChange}
			onConnect={onConnect}
			onEdgesDelete={onEdgesDelete}
			onNodeDragStop={onNodeDragStop}
			isValidConnection={isValidConnection}
			nodeTypes={nodeTypes}
			edgeTypes={edgeTypes}
			defaultEdgeOptions={{ ...defaultEdgeOptions, style: { stroke: theme.palette.primary.main, strokeWidth: 2 } }}
			fitView
			fitViewOptions={{ padding: 0.2, duration: 800 }}
			minZoom={0.5}
			maxZoom={2}
			nodesDraggable
			nodesConnectable
			elementsSelectable
			style={{ width: '100%', height: '100%', backgroundColor: canvasColor, ...graphVariables }}
			className="environments-react-flow"
		>
			<Controls />
			<Background bgColor="transparent" color={alpha(flowTokens.grid, 0.35)} gap={20} />
		</ReactFlow>
	);
};

export default function EnvironmentsView() {
	const theme = useTheme();
	const isMobile = useIsMobile();
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const toastService = useToastService();
	const queryClient = useQueryClient();

	const [openCreateDialog, setOpenCreateDialog] = useState(false);
	const [createAfterEnvironmentId, setCreateAfterEnvironmentId] = useState<string | null>(null);
	const [lastSelectedCloudProvider, setLastSelectedCloudProvider] = useState<CloudProviderType | ''>('');
	const [openEditDialog, setOpenEditDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openPromoteDialog, setOpenPromoteDialog] = useState(false);
	const [selectedEnvironment, setSelectedEnvironment] = useState<EnvironmentModel | null>(null);
	const [environmentToDelete, setEnvironmentToDelete] = useState<EnvironmentModel | null>(null);
	const [promoteData, setPromoteData] = useState<{ sourceId: string; targetId: string; sourceEnv?: EnvironmentModel; targetEnv?: EnvironmentModel } | null>(
		null,
	);
	const [pendingClientOperation, setPendingClientOperation] = useState<{
		operation: 'addClient' | 'redeployClient' | 'removeClient' | 'rotateClientSecret';
		environment: EnvironmentModel;
		client: ClientModel;
	} | null>(null);
	const [pendingApiKeyOperation, setPendingApiKeyOperation] = useState<{
		operation: 'addApiKey' | 'redeployApiKey' | 'removeApiKey' | 'rotateApiKeySecret';
		environment: EnvironmentModel;
		apiKey: ApiKeyModel;
	} | null>(null);

	// Component operation dialog state
	const [componentOperationData, setComponentOperationData] = useState<{
		operation: 'addComponent' | 'updateComponent' | 'removeComponent' | 'redeployComponent' | 'deleteComponent';
		environment: EnvironmentModel;
		component: ComponentModel;
		currentVersion?: string;
	} | null>(null);
	const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
	const [persistedGraphLayers, setGraphLayers] = usePersistedSetting<EnvironmentGraphLayer[] | EnvironmentGraphLayer>(
		'environments.graphViewMode',
		DEFAULT_GRAPH_LAYERS,
	);
	const graphLayers = useMemo(() => normalizeGraphLayers(persistedGraphLayers), [persistedGraphLayers]);

	// Panel State
	const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string | null>(null);
	useNotificationSubscription('environment', selectedEnvironmentId);
	const [selectedReleasePlan, setSelectedReleasePlan] = useState<string | null>(null);
	const [showReleasePlanDetails, setShowReleasePlanDetails] = useState(false);
	const [releasePlansRefreshTrigger, setReleasePlansRefreshTrigger] = useState(0);
	const [releasePlansPanelCollapsed, setReleasePlansPanelCollapsed] = useState(false);
	const [releasePlansPanelWidth, setReleasePlansPanelWidth] = useState(RELEASE_PLANS_PANEL_DEFAULT_WIDTH);

	// Component Details Panel State
	const [selectedComponentForDetails, setSelectedComponentForDetails] = useState<{
		component: any;
		environmentComponent: any;
		environment?: EnvironmentModel;
	} | null>(null);

	// Container Images Panel State
	const [selectedContainerImageId, setSelectedContainerImageId] = useState<string | null>(null);
	const [showContainerImageDetails, setShowContainerImageDetails] = useState(false);
	const [containerImagesRefreshTrigger, setContainerImagesRefreshTrigger] = useState(0);
	const [containerImagesPanelCollapsed, setContainerImagesPanelCollapsed] = useState(false);
	const [containerImagesPanelWidth, setContainerImagesPanelWidth] = useState(CONTAINER_IMAGES_PANEL_DEFAULT_WIDTH);

	// Components Panel State
	const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
	const [showComponentPanelDetails, setShowComponentPanelDetails] = useState(false);
	const [componentsPanelCollapsed, setComponentsPanelCollapsed] = useState(false);
	const [componentsPanelWidth, setComponentsPanelWidth] = useState(COMPONENTS_PANEL_DEFAULT_WIDTH);

	// Clients Panel State
	const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
	const [selectedClientForDetails, setSelectedClientForDetails] = useState<ClientModel | null>(null);
	const [selectedEnvironmentForClientDetails, setSelectedEnvironmentForClientDetails] = useState<EnvironmentModel | null>(null);
	const [selectedEnvironmentClientForDetails, setSelectedEnvironmentClientForDetails] = useState<EnvironmentClientModel | null>(null);
	const [showClientDetails, setShowClientDetails] = useState(false);
	const [clientsPanelCollapsed, setClientsPanelCollapsed] = useState(false);
	const [clientsPanelWidth, setClientsPanelWidth] = useState(CLIENTS_PANEL_DEFAULT_WIDTH);

	// API Keys Panel State
	const [selectedApiKeyId, setSelectedApiKeyId] = useState<string | null>(null);
	const [selectedApiKeyForDetails, setSelectedApiKeyForDetails] = useState<ApiKeyModel | null>(null);
	const [selectedEnvironmentForApiKeyDetails, setSelectedEnvironmentForApiKeyDetails] = useState<EnvironmentModel | null>(null);
	const [selectedEnvironmentApiKeyForDetails, setSelectedEnvironmentApiKeyForDetails] = useState<EnvironmentApiKeyModel | null>(null);
	const [showApiKeyDetails, setShowApiKeyDetails] = useState(false);
	const [apiKeysPanelCollapsed, setApiKeysPanelCollapsed] = useState(false);
	const [apiKeysPanelWidth, setApiKeysPanelWidth] = useState(API_KEYS_PANEL_DEFAULT_WIDTH);
	const [embedSelection, setEmbedSelection] = useState<{ environment?: EnvironmentModel; definitionId?: string; deploymentId?: string; action?: 'remove' }>({});
	const [activePanelFamily, dispatchPanelFamily] = useReducer(environmentPanelFamilyReducer, null);

	useEffect(() => {
		if (isMobile) {
			setReleasePlansPanelCollapsed(false);
			setContainerImagesPanelCollapsed(false);
			setComponentsPanelCollapsed(false);
			setClientsPanelCollapsed(false);
			setApiKeysPanelCollapsed(false);
		}
	}, [isMobile]);

	const closeReleasePlanDetails = useCallback(() => {
		setShowReleasePlanDetails(false);
		setSelectedReleasePlan(null);
	}, []);

	const closeEnvironmentDetails = useCallback(() => {}, []);

	const closeEnvironmentComponentDetails = useCallback(() => {
		setSelectedComponentForDetails(null);
	}, []);

	const closeComponentDetails = useCallback(() => {
		setShowComponentPanelDetails(false);
		setSelectedComponentId(null);
	}, []);

	const closeClientDetails = useCallback(() => {
		setShowClientDetails(false);
		setSelectedClientId(null);
		setSelectedClientForDetails(null);
		setSelectedEnvironmentForClientDetails(null);
		setSelectedEnvironmentClientForDetails(null);
	}, []);

	const closeApiKeyDetails = useCallback(() => {
		setShowApiKeyDetails(false);
		setSelectedApiKeyId(null);
		setSelectedApiKeyForDetails(null);
		setSelectedEnvironmentForApiKeyDetails(null);
		setSelectedEnvironmentApiKeyForDetails(null);
	}, []);

	const closePanelFamilies = useCallback((except?: EnvironmentPanelFamily) => {
		dispatchPanelFamily(except ? { type: 'open', family: except } : { type: 'close' });
		if (except !== 'releasePlans') {
			setShowReleasePlanDetails(false);
			setSelectedReleasePlan(null);
		}

		if (except !== 'environmentComponent') {
			setSelectedComponentForDetails(null);
		}

		if (except !== 'containerImages') {
			setShowContainerImageDetails(false);
			setSelectedContainerImageId(null);
		}

		if (except !== 'components') {
			setShowComponentPanelDetails(false);
			setSelectedComponentId(null);
		}

		if (except !== 'clients') {
			setShowClientDetails(false);
			setSelectedClientId(null);
			setSelectedClientForDetails(null);
			setSelectedEnvironmentForClientDetails(null);
			setSelectedEnvironmentClientForDetails(null);
		}

		if (except !== 'apiKeys') {
			setShowApiKeyDetails(false);
			setSelectedApiKeyId(null);
			setSelectedApiKeyForDetails(null);
			setSelectedEnvironmentForApiKeyDetails(null);
			setSelectedEnvironmentApiKeyForDetails(null);
		}

		if (except !== 'releasePlans' && except !== 'environment') {
			setSelectedEnvironmentId(null);
		}
	}, []);

	// Query to fetch environments using the shared hook
	const { data: environmentsData, isLoading, refetch } = useApiEnvironments();

	// Query to fetch components
	const { data: componentsData, isLoading: componentsLoading } = useQuery({
		queryKey: ['components'],
		queryFn: async () => {
			// Get all components - using a large page size to get all
			const result = await client.searchComponent({});
			return result;
		},
	});

	const contextClient = useApiClient(ContextClient);
	const subdomains = useQuery({ queryKey: contextDesignerQueryKeys.subDomains(), enabled: graphLayers.includes('components') && !!componentsData?.items.some(component => !!component.contextId), queryFn: ({ signal }) => contextClient.getSubDomains(signal) });
	const contextSubdomains = useMemo(() => new Map(subdomains.data?.flatMap(entry => entry.contexts.map(context => [context.id, entry.subDomain] as const)) ?? []), [subdomains.data]);

	const { data: clientsData, isLoading: clientsLoading } = useApiClients();
	const { data: apiKeysData, isLoading: apiKeysLoading } = useApiKeys();

	// Compute selected environment from the current data using useMemo
	const selectedEnvironmentById = useMemo(() => {
		if (!selectedEnvironmentId || !environmentsData) return null;
		return environmentsData.find((env) => env.id === selectedEnvironmentId) || null;
	}, [selectedEnvironmentId, environmentsData]);

	// Computed environment objects for different panels
	const selectedEnvironmentForPlans = activePanelFamily === 'releasePlans' ? selectedEnvironmentById : null;
	const selectedEnvironmentForDetails = activePanelFamily === 'environment' ? selectedEnvironmentById : null;

	const handleEditEnvironment = useCallback((environment: EnvironmentModel) => {
		setSelectedEnvironment(environment);
		setOpenEditDialog(true);
	}, []);

	const handleDeleteEnvironment = useCallback(async (environment: EnvironmentModel) => {
		setEnvironmentToDelete(environment);
		setOpenDeleteDialog(true);
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (!environmentToDelete) return;

		try {
			await client.delete(environmentToDelete.id);
			toastService.success(`Environment "${environmentToDelete.name}" deleted successfully`);
			refetch();
		} catch (error) {
			console.error('Error deleting environment:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to delete environment: ${errorMessage}`);
		} finally {
			setOpenDeleteDialog(false);
			setEnvironmentToDelete(null);
		}
	}, [environmentToDelete, client, toastService, refetch]);

	const handleCancelDelete = useCallback(() => {
		setOpenDeleteDialog(false);
		setEnvironmentToDelete(null);
	}, []);

	// Helper method to invalidate queries and refresh data
	const refreshData = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: ['environments'] });
		await queryClient.invalidateQueries({ queryKey: ['components'] });
		await queryClient.invalidateQueries({ queryKey: contextDesignerQueryKeys.subDomains() });
		await queryClient.invalidateQueries({ queryKey: ['clients'] });
		await queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
		await queryClient.invalidateQueries({ queryKey: ['embedDeployments'] });
	}, [queryClient]);

	const updateEnvironmentInCache = useCallback(
		(environmentId: string, update: Partial<EnvironmentModel>) => {
			queryClient.setQueryData<EnvironmentModel[]>(
				['environments'],
				(current) => current?.map((environment) => (environment.id === environmentId ? { ...environment, ...update } : environment)) ?? current,
			);
		},
		[queryClient],
	);

	const persistEnvironmentGraphUpdate = useCallback(
		async (environment: EnvironmentModel, update: { metadata?: EnvironmentModel['metadata']; transitions?: EnvironmentTransition[] }) => {
			const previousEnvironment = environment;
			const nextEnvironment = { ...environment, ...update };
			const nextTransitions = (update.transitions ?? environment.transitions ?? []).map(toEnvironmentTransition);
			updateEnvironmentInCache(environment.id, update as Partial<EnvironmentModel>);

			try {
				await client.update(environment.id, {
					metadata: nextEnvironment.metadata,
					transitions: nextTransitions,
				});
			} catch (error) {
				console.error('Error saving environment graph:', error);
				updateEnvironmentInCache(environment.id, previousEnvironment);
				toastService.error('Failed to save environment graph changes');
			}
		},
		[client, toastService, updateEnvironmentInCache],
	);

	const initializeEnvironmentGraph = useCallback(async () => {
		if (!environmentsData || environmentsData.length === 0 || hasInitializedEnvironmentGraph(environmentsData)) {
			return true;
		}

		const transitionUpdates = getLegacyTransitionUpdates(environmentsData);
		try {
			await Promise.all(
				environmentsData.map((environment) => {
					const update = {
						transitions: transitionUpdates.get(environment.id) ?? [],
						metadata: {
							...(environment.metadata ?? {}),
							[ENVIRONMENT_GRAPH_INITIALIZED_METADATA_KEY]: true,
						},
					};

					return client.update(environment.id, update);
				}),
			);
			await refetch();
			return true;
		} catch (error) {
			console.error('Error initializing environment graph:', error);
			await refetch();
			toastService.error('Failed to prepare the environment graph');
			return false;
		}
	}, [client, environmentsData, refetch, toastService]);

	const handleOpenCreateEnvironment = useCallback(
		async (predecessorId: string | null = null) => {
			if (!(await initializeEnvironmentGraph())) {
				return;
			}

			setCreateAfterEnvironmentId(predecessorId);
			setOpenCreateDialog(true);
		},
		[initializeEnvironmentGraph],
	);

	const handleCreateDialogOpenChange = useCallback((open: boolean) => {
		setOpenCreateDialog(open);
		if (!open) {
			setCreateAfterEnvironmentId(null);
		}
	}, []);

	const handleEnvironmentNodeDragStop = useCallback(
		(_event: React.MouseEvent, node: Node) => {
			const environment = environmentsData?.find((env) => env.id === node.id);
			if (!environment) {
				return;
			}

			void persistEnvironmentGraphUpdate(environment, {
				metadata: {
					...(environment.metadata ?? {}),
					x: node.position.x,
					y: node.position.y,
				},
			});
		},
		[environmentsData, persistEnvironmentGraphUpdate],
	);

	const isValidEnvironmentConnection = useCallback(
		(connection: Connection | Edge) => {
			if (!connection.source || !connection.target) {
				return false;
			}

			if (connection.source === connection.target) {
				return false;
			}

			const source = environmentsData?.find((env) => env.id === connection.source);
			const target = environmentsData?.find((env) => env.id === connection.target);
			if (!source || !target) {
				return false;
			}

			return (
				!edges.some((edge) => edge.source === source.id && edge.target === target.id) &&
				!(source.transitions ?? []).some((transition) => transition.to === target.id)
			);
		},
		[edges, environmentsData],
	);

	const handleEnvironmentConnect = useCallback(
		(connection: Connection) => {
			if (!connection.source || !connection.target || !isValidEnvironmentConnection(connection)) {
				return;
			}

			const sourceEnvironment = environmentsData?.find((env) => env.id === connection.source);
			if (!sourceEnvironment) {
				return;
			}

			if (!hasInitializedEnvironmentGraph(environmentsData)) {
				const transitionUpdates = getLegacyTransitionUpdates(environmentsData ?? []);
				const sourceTransitions = transitionUpdates.get(sourceEnvironment.id) ?? [];
				sourceTransitions.push(makeEnvironmentTransition(connection.source, connection.target, sourceTransitions.length));
				transitionUpdates.set(sourceEnvironment.id, sourceTransitions);

				void Promise.all(
					(environmentsData ?? []).map((environment) =>
						persistEnvironmentGraphUpdate(environment, {
							transitions: transitionUpdates.get(environment.id) ?? [],
							metadata: {
								...(environment.metadata ?? {}),
								[ENVIRONMENT_GRAPH_INITIALIZED_METADATA_KEY]: true,
							},
						}),
					),
				);
				return;
			}

			const transitions = (sourceEnvironment.transitions ?? []).map(toEnvironmentTransition);
			const nextOrder = transitions.length === 0 ? 0 : Math.max(...transitions.map((transition) => transition.order ?? 0)) + 1;
			void persistEnvironmentGraphUpdate(sourceEnvironment, {
				transitions: [
					...transitions,
					{
						id: createEnvironmentTransitionId(connection.source, connection.target),
						to: connection.target,
						order: nextOrder,
						metadata: {
							sourceHandle: connection.sourceHandle,
							targetHandle: connection.targetHandle,
						},
					},
				],
				metadata: {
					...(sourceEnvironment.metadata ?? {}),
					[ENVIRONMENT_GRAPH_INITIALIZED_METADATA_KEY]: true,
				},
			});
		},
		[environmentsData, isValidEnvironmentConnection, persistEnvironmentGraphUpdate],
	);

	const handleEnvironmentEdgesDelete = useCallback(
		(deletedEdges: Edge[]) => {
			if (!environmentsData || deletedEdges.length === 0) {
				return;
			}

			const deletedTransitionKeys = new Set(deletedEdges.map((edge) => `${edge.source}:${edge.target}`));
			if (!hasInitializedEnvironmentGraph(environmentsData)) {
				const transitionUpdates = getLegacyTransitionUpdates(environmentsData, deletedTransitionKeys);
				void Promise.all(
					environmentsData.map((environment) =>
						persistEnvironmentGraphUpdate(environment, {
							transitions: transitionUpdates.get(environment.id) ?? [],
							metadata: {
								...(environment.metadata ?? {}),
								[ENVIRONMENT_GRAPH_INITIALIZED_METADATA_KEY]: true,
							},
						}),
					),
				);
				return;
			}

			const deletedTargetsBySource = deletedEdges.reduce((result, edge) => {
				const targets = result.get(edge.source) ?? new Set<string>();
				targets.add(edge.target);
				result.set(edge.source, targets);
				return result;
			}, new Map<string, Set<string>>());

			Array.from(deletedTargetsBySource.entries()).forEach(([sourceId, targetIds]) => {
				const sourceEnvironment = environmentsData.find((env) => env.id === sourceId);
				if (!sourceEnvironment) {
					return;
				}

				void persistEnvironmentGraphUpdate(sourceEnvironment, {
					transitions: (sourceEnvironment.transitions ?? []).filter((transition) => !targetIds.has(transition.to)).map(toEnvironmentTransition),
					metadata: {
						...(sourceEnvironment.metadata ?? {}),
						[ENVIRONMENT_GRAPH_INITIALIZED_METADATA_KEY]: true,
					},
				});
			});
		},
		[environmentsData, persistEnvironmentGraphUpdate],
	);

	const showClientReleasePlan = useCallback(
		(releasePlanId: string, environment: EnvironmentModel) => {
			closePanelFamilies('releasePlans');
			setSelectedEnvironmentId(environment.id);
			setSelectedReleasePlan(releasePlanId);
			setShowReleasePlanDetails(true);
			setReleasePlansRefreshTrigger((prev) => prev + 1);
		},
		[closePanelFamilies],
	);

	const { deployClient, removeClient, rotateClientSecret } = useEnvironmentClientActions({
		onReleasePlanCreated: showClientReleasePlan,
	});
	const { deployApiKey, removeApiKey, rotateApiKeySecret } = useEnvironmentApiKeyActions({
		onReleasePlanCreated: showClientReleasePlan,
	});

	// Specialized refresh handler for component details that also updates the selected component
	const refreshComponentDetails = useCallback(async () => {
		if (!selectedComponentForDetails) return;

		// Invalidate and wait for refetch
		await queryClient.invalidateQueries({ queryKey: ['environments'] });
		await queryClient.invalidateQueries({ queryKey: ['components'] });

		// Wait for the queries to refetch by using refetchQueries
		await queryClient.refetchQueries({ queryKey: ['environments'] });
		await queryClient.refetchQueries({ queryKey: ['components'] });

		// Get the fresh data after refetch
		const freshEnvironmentsData = queryClient.getQueryData<EnvironmentModel[]>(['environments']);
		const freshComponentsData = queryClient.getQueryData(['components']) as any;

		if (freshEnvironmentsData && freshComponentsData) {
			// Find the updated environment
			const updatedEnvironment = freshEnvironmentsData.find((env) => env.id === selectedComponentForDetails.environment?.id);

			if (updatedEnvironment) {
				// Find the updated environmentComponent
				const updatedEnvComponent = updatedEnvironment.components?.find((ec) => ec.id === selectedComponentForDetails.environmentComponent.id);

				// Find the updated component
				const updatedComponent = freshComponentsData.items?.find((c: ComponentModel) => c.id === selectedComponentForDetails.component.id);

				if (updatedEnvComponent && updatedComponent) {
					// Update the state with fresh data
					setSelectedComponentForDetails({
						component: updatedComponent,
						environmentComponent: updatedEnvComponent,
						environment: updatedEnvironment,
					});
				}
			}
		}
	}, [queryClient, selectedComponentForDetails]);

	// Handle release plan status changes - refresh environment data
	const handleReleasePlanStatusChange = useCallback(async () => {
		await refreshData();
	}, [refreshData]);

	// Container Images Panel Handlers
	const handleOpenContainerImagesPanel = useCallback(() => {
		closePanelFamilies('containerImages');
	}, [closePanelFamilies]);

	const handleCloseContainerImagesPanel = useCallback(() => {
		setShowContainerImageDetails(false);
		setSelectedContainerImageId(null);
		dispatchPanelFamily({ type: 'close' });
	}, []);

	const handleContainerImageSelect = useCallback(
		(imageId: string) => {
			closePanelFamilies('containerImages');
			setSelectedContainerImageId(imageId);
			setShowContainerImageDetails(true);
		},
		[closePanelFamilies],
	);

	const handleCloseContainerImageDetails = useCallback(() => {
		setShowContainerImageDetails(false);
		setSelectedContainerImageId(null);
	}, []);

	// Components Panel Handlers
	const handleOpenComponentsPanel = useCallback(() => {
		closePanelFamilies('components');
	}, [closePanelFamilies]);

	const handleCloseComponentsPanel = useCallback(() => {
		setShowComponentPanelDetails(false);
		setSelectedComponentId(null);
		dispatchPanelFamily({ type: 'close' });
	}, []);

	const handleComponentSelect = useCallback(
		(componentId: string) => {
			closePanelFamilies('components');
			setSelectedComponentId(componentId);
			setShowComponentPanelDetails(true);
		},
		[closePanelFamilies],
	);

	const handleCloseComponentPanelDetails = useCallback(() => {
		closeComponentDetails();
	}, [closeComponentDetails]);

	const handleOpenClientsPanel = useCallback(() => {
		closePanelFamilies('clients');
	}, [closePanelFamilies]);

	const handleCloseClientsPanel = useCallback(() => {
		setShowClientDetails(false);
		setSelectedClientId(null);
		dispatchPanelFamily({ type: 'close' });
	}, []);

	const handleClientSelect = useCallback(
		(clientId: string) => {
			closePanelFamilies('clients');
			setSelectedClientId(clientId);
			setSelectedEnvironmentClientForDetails(null);
			setShowClientDetails(true);
		},
		[closePanelFamilies],
	);

	const handleCloseClientDetails = useCallback(() => {
		closeClientDetails();
	}, [closeClientDetails]);

	const handleOpenApiKeysPanel = useCallback(() => {
		closePanelFamilies('apiKeys');
	}, [closePanelFamilies]);

	const handleCloseApiKeysPanel = useCallback(() => {
		setShowApiKeyDetails(false);
		setSelectedApiKeyId(null);
		dispatchPanelFamily({ type: 'close' });
	}, []);

	const handleApiKeySelect = useCallback(
		(apiKeyId: string) => {
			closePanelFamilies('apiKeys');
			setSelectedApiKeyId(apiKeyId);
			setSelectedEnvironmentApiKeyForDetails(null);
			setShowApiKeyDetails(true);
		},
		[closePanelFamilies],
	);

	const handleCloseApiKeyDetails = useCallback(() => {
		closeApiKeyDetails();
	}, [closeApiKeyDetails]);

	const handleContainerImageVersionAdded = useCallback(() => {
		// Refresh the container images list
		setContainerImagesRefreshTrigger((prev) => prev + 1);
	}, []);

	// Handle adding/updating a component (shows confirmation dialog)
	const handleAddEnvironmentComponent = useCallback(
		(environment: EnvironmentModel, component: ComponentModel, operation: 'addComponent' | 'updateComponent' | 'redeployComponent') => {
			// For update and redeploy operations, get the current version
			const currentVersion =
				operation === 'updateComponent' || operation === 'redeployComponent'
					? environment.components?.find((c) => c.componentId === component.id)?.containerImageVersion
					: undefined;

			setComponentOperationData({
				operation,
				environment,
				component,
				currentVersion,
			});
			setOpenPromoteDialog(true);
		},
		[],
	);

	// Handle removing a component from an environment (shows confirmation dialog)
	const handleRemoveEnvironmentComponent = useCallback((environment: EnvironmentModel, component: ComponentModel) => {
		setComponentOperationData({
			operation: 'removeComponent',
			environment,
			component,
		});
		setOpenPromoteDialog(true);
	}, []);

	// Handle deleting a component completely (shows confirmation dialog)
	const handleDeleteComponent = useCallback(
		(component: ComponentModel) => {
			setComponentOperationData({
				operation: 'deleteComponent',
				environment: environmentsData?.[0] || ({} as EnvironmentModel), // Dummy environment for delete operation
				component,
			});
			setOpenPromoteDialog(true);
		},
		[environmentsData],
	);

	const handleAddEnvironmentClient = useCallback((environment: EnvironmentModel, selectedClient: ClientModel, operation: 'addClient' | 'redeployClient') => {
		setPendingClientOperation({ operation, environment, client: selectedClient });
	}, []);

	const handleRemoveEnvironmentClient = useCallback((environment: EnvironmentModel, selectedClient: ClientModel) => {
		setPendingClientOperation({ operation: 'removeClient', environment, client: selectedClient });
	}, []);

	const handleRotateEnvironmentClientSecret = useCallback((environment: EnvironmentModel, selectedClient: ClientModel) => {
		setPendingClientOperation({ operation: 'rotateClientSecret', environment, client: selectedClient });
	}, []);

	const handleAddEnvironmentApiKey = useCallback((environment: EnvironmentModel, selectedApiKey: ApiKeyModel, operation: 'addApiKey' | 'redeployApiKey') => {
		setPendingApiKeyOperation({ operation, environment, apiKey: selectedApiKey });
	}, []);

	const handleRemoveEnvironmentApiKey = useCallback((environment: EnvironmentModel, selectedApiKey: ApiKeyModel) => {
		setPendingApiKeyOperation({ operation: 'removeApiKey', environment, apiKey: selectedApiKey });
	}, []);

	const handleRotateEnvironmentApiKeySecret = useCallback((environment: EnvironmentModel, selectedApiKey: ApiKeyModel) => {
		setPendingApiKeyOperation({ operation: 'rotateApiKeySecret', environment, apiKey: selectedApiKey });
	}, []);

	const handleConfirmClientOperation = useCallback(async () => {
		if (!pendingClientOperation) return;

		try {
			if (pendingClientOperation.operation === 'removeClient') {
				await removeClient(pendingClientOperation.environment, pendingClientOperation.client);
			} else if (pendingClientOperation.operation === 'rotateClientSecret') {
				await rotateClientSecret(pendingClientOperation.environment, pendingClientOperation.client);
			} else {
				await deployClient(pendingClientOperation.environment, pendingClientOperation.client, pendingClientOperation.operation);
			}
		} finally {
			setPendingClientOperation(null);
		}
	}, [deployClient, pendingClientOperation, removeClient, rotateClientSecret]);

	const handleConfirmApiKeyOperation = useCallback(async () => {
		if (!pendingApiKeyOperation) return;

		try {
			if (pendingApiKeyOperation.operation === 'removeApiKey') {
				await removeApiKey(pendingApiKeyOperation.environment, pendingApiKeyOperation.apiKey);
			} else if (pendingApiKeyOperation.operation === 'rotateApiKeySecret') {
				await rotateApiKeySecret(pendingApiKeyOperation.environment, pendingApiKeyOperation.apiKey);
			} else {
				await deployApiKey(pendingApiKeyOperation.environment, pendingApiKeyOperation.apiKey, pendingApiKeyOperation.operation);
			}
		} finally {
			setPendingApiKeyOperation(null);
		}
	}, [deployApiKey, pendingApiKeyOperation, removeApiKey, rotateApiKeySecret]);

	// Handle promote action between environments
	const handlePromote = useCallback(
		(sourceEnvironmentId: string, targetEnvironmentId: string) => {
			// Find the source and target environments
			const sourceEnv = environmentsData?.find((env) => env.id === sourceEnvironmentId);
			const targetEnv = environmentsData?.find((env) => env.id === targetEnvironmentId);

			setPromoteData({
				sourceId: sourceEnvironmentId,
				targetId: targetEnvironmentId,
				sourceEnv,
				targetEnv,
			});
			setOpenPromoteDialog(true);
		},
		[environmentsData],
	);

	const handlePromoteClose = useCallback(() => {
		setOpenPromoteDialog(false);
		setPromoteData(null);
		setComponentOperationData(null);
	}, []);

	const handlePromoteSuccess = useCallback(
		async (releasePlanId?: string, targetEnvironment?: EnvironmentModel) => {
			// Close the dialog
			setOpenPromoteDialog(false);
			setPromoteData(null);
			setComponentOperationData(null);

			// Refresh the environments data
			await refreshData();

			// Open Release Plans panel and select the created release plan
			if (releasePlanId && targetEnvironment) {
				closePanelFamilies('releasePlans');
				setSelectedEnvironmentId(targetEnvironment.id);
				setSelectedReleasePlan(releasePlanId);
				setShowReleasePlanDetails(true); // Also open the details panel
				// Trigger refresh of the release plans panel
				setReleasePlansRefreshTrigger((prev) => prev + 1);
			}
		},
		[closePanelFamilies, refreshData],
	);

	const handleViewEmbeds = useCallback((environment?: EnvironmentModel, definitionId?: string, deploymentId?: string, action?: 'remove') => {
        closePanelFamilies('embeds');
        setEmbedSelection({ environment, definitionId, deploymentId, action });
    }, [closePanelFamilies]);

	// Handle viewing release plans
	const handleViewReleases = useCallback(
		(environment: EnvironmentModel) => {
			closePanelFamilies('releasePlans');
			setSelectedEnvironmentId(environment.id);
			setShowReleasePlanDetails(false);
			setSelectedReleasePlan(null);
		},
		[closePanelFamilies],
	);

	const handleViewAllReleases = useCallback(() => {
		closePanelFamilies('releasePlans');
		setSelectedEnvironmentId(null);
		setShowReleasePlanDetails(false);
		setSelectedReleasePlan(null);
	}, [closePanelFamilies]);

	// Handle release plan selection
	const handleReleasePlanSelect = useCallback(
		(releasePlanId: string) => {
			closePanelFamilies('releasePlans');
			setSelectedReleasePlan(releasePlanId);
			setShowReleasePlanDetails(true);
		},
		[closePanelFamilies],
	);

	// Handle closing release plans panel
	const handleCloseReleasePlansPanel = useCallback(() => {
		setShowReleasePlanDetails(false);
		setSelectedReleasePlan(null);
		setSelectedEnvironmentId(null);
		dispatchPanelFamily({ type: 'close' });
	}, []);

	// Handle closing release plan details panel
	const handleCloseReleasePlanDetails = useCallback(() => {
		closeReleasePlanDetails();
	}, [closeReleasePlanDetails]);

	// Handle release plan deletion
	const handleReleasePlanDelete = useCallback(
		(deletedPlanId: string) => {
			// If the deleted plan is currently selected, close the details panel
			if (selectedReleasePlan === deletedPlanId) {
				setShowReleasePlanDetails(false);
				setSelectedReleasePlan(null);
			}
		},
		[selectedReleasePlan],
	);

	// Handle environment details panel
	const handleViewEnvironmentDetails = useCallback(
		(environment: EnvironmentModel) => {
			closePanelFamilies('environment');
			setSelectedEnvironmentId(environment.id);
		},
		[closePanelFamilies],
	);

	const handleCloseEnvironmentDetails = useCallback(() => {
		closeEnvironmentDetails();
		setSelectedEnvironmentId(null);
		dispatchPanelFamily({ type: 'close' });
	}, [closeEnvironmentDetails]);

	// Handle component details panel
	const handleViewComponentDetails = useCallback(
		(component: any, environmentComponent: any, environment: EnvironmentModel) => {
			closePanelFamilies('environmentComponent');
			setSelectedComponentForDetails({ component, environmentComponent, environment });
			setSelectedEnvironmentId(null);
		},
		[closePanelFamilies],
	);

	const handleViewClientNodeDetails = useCallback(
		(selectedClient: ClientModel, environmentClient?: EnvironmentClientModel, environment?: EnvironmentModel) => {
			closePanelFamilies('clients');
			setSelectedClientId(selectedClient.id);
			setSelectedClientForDetails(selectedClient);
			setSelectedEnvironmentForClientDetails(environment ?? null);
			setSelectedEnvironmentClientForDetails(environmentClient ?? null);
			setShowClientDetails(true);
			setSelectedEnvironmentId(null);
		},
		[closePanelFamilies],
	);

	const handleViewApiKeyNodeDetails = useCallback(
		(selectedApiKey: ApiKeyModel, environmentApiKey?: EnvironmentApiKeyModel, environment?: EnvironmentModel) => {
			closePanelFamilies('apiKeys');
			setSelectedApiKeyId(selectedApiKey.id);
			setSelectedApiKeyForDetails(selectedApiKey);
			setSelectedEnvironmentForApiKeyDetails(environment ?? null);
			setSelectedEnvironmentApiKeyForDetails(environmentApiKey ?? null);
			setShowApiKeyDetails(true);
			setSelectedEnvironmentId(null);
		},
		[closePanelFamilies],
	);

	const handleCloseComponentDetails = useCallback(() => {
		closeEnvironmentComponentDetails();
		dispatchPanelFamily({ type: 'close' });
	}, [closeEnvironmentComponentDetails]);

	// Calculate the next order for new environments
	const nextEnvironmentOrder = useMemo(() => {
		if (!environmentsData || environmentsData.length === 0) return 1;
		const maxOrder = Math.max(...environmentsData.map((env) => env.order || 0));
		return maxOrder + 1;
	}, [environmentsData]);

	const defaultCreateCloudProvider = useMemo(() => {
		if (lastSelectedCloudProvider) {
			return lastSelectedCloudProvider;
		}

		if (!environmentsData || environmentsData.length === 0) {
			return '';
		}

		const sortedEnvironments = [...environmentsData].sort((a, b) => (a.order || 0) - (b.order || 0));
		const lastEnvironment = sortedEnvironments[sortedEnvironments.length - 1];
		return getEnvironmentCloudProvider(lastEnvironment?.attributes)?.type || '';
	}, [environmentsData, lastSelectedCloudProvider]);

	const createEnvironmentMetadata = useMemo(() => ({ [ENVIRONMENT_GRAPH_INITIALIZED_METADATA_KEY]: true }), []);

	const handleGraphLayersChange = useCallback(
		(_event: React.SyntheticEvent, nextLayers: EnvironmentGraphLayer[]) => {
			if (!nextLayers.includes('components')) {
				closeEnvironmentComponentDetails();
				closeComponentDetails();
			}

			if (!nextLayers.includes('clients')) {
				closeClientDetails();
			}

			if (!nextLayers.includes('apiKeys')) {
				closeApiKeyDetails();
			}

			setGraphLayers(nextLayers);
		},
		[closeApiKeyDetails, closeClientDetails, closeComponentDetails, closeEnvironmentComponentDetails, setGraphLayers],
	);

	// Convert environment data to React Flow nodes and edges
	const { flowNodes, flowEdges } = useMemo(() => {
		if (!environmentsData) return { flowNodes: [], flowEdges: [] };

		// Get components list (default to empty if not loaded yet)
		const components = componentsData?.items || [];
		const clients = clientsData || [];
		const apiKeys = apiKeysData || [];

		const sortedEnvironments = [...environmentsData].sort((a, b) => (a.order || 0) - (b.order || 0));
		const environmentById = new Map(environmentsData.map((environment) => [environment.id, environment]));
		const hasExplicitTransitions = hasInitializedEnvironmentGraph(environmentsData);

		const nodes: Node[] = [];
		const edges: Edge[] = [];

		// If no environments exist, show placeholder node
		if (sortedEnvironments.length === 0) {
			nodes.push({
				id: 'create-environment-placeholder',
				type: 'createEnvironmentPlaceholder',
				position: { x: DEFAULT_NODE_START_X, y: DEFAULT_NODE_Y },
				data: {
					onCreate: () => void handleOpenCreateEnvironment(),
				},
				draggable: false,
			});
			return { flowNodes: nodes, flowEdges: edges };
		}

		if (hasExplicitTransitions) {
			sortedEnvironments.forEach((env) => {
				(env.transitions ?? [])
					.filter((transition) => environmentById.has(transition.to))
					.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
					.forEach((transition) => {
						edges.push({
							id: transition.id || `edge-${env.id}-${transition.to}`,
							source: env.id,
							target: transition.to,
							type: 'promote',
							data: {
								onPromote: handlePromote,
								sourceEnvironmentId: env.id,
								targetEnvironmentId: transition.to,
							},
							style: {
								stroke: theme.palette.primary.main,
								strokeWidth: 2,
							},
							markerEnd: {
								type: MarkerType.ArrowClosed,
								color: theme.palette.primary.main,
							},
						});
					});
			});
		} else {
			sortedEnvironments.forEach((env, index) => {
				if (index === 0) {
					return;
				}

				const sourceEnvironment = sortedEnvironments[index - 1];
				edges.push({
					id: `edge-${sourceEnvironment.id}-${env.id}`,
					source: sourceEnvironment.id,
					target: env.id,
					type: 'promote',
					data: {
						onPromote: handlePromote,
						sourceEnvironmentId: sourceEnvironment.id,
						targetEnvironmentId: env.id,
					},
					style: {
						stroke: theme.palette.primary.main,
						strokeWidth: 2,
					},
					markerEnd: {
						type: MarkerType.ArrowClosed,
						color: theme.palette.primary.main,
					},
				});
			});
		}

		const incomingEnvironmentIds = new Set(edges.map((edge) => edge.target));
		const outgoingEnvironmentIds = new Set(edges.map((edge) => edge.source));

		// Create environment nodes
		sortedEnvironments.forEach((env, index) => {
			const x = getMetadataNumber(env.metadata, 'x') ?? DEFAULT_NODE_START_X + index * DEFAULT_NODE_SPACING;
			const y = getMetadataNumber(env.metadata, 'y') ?? DEFAULT_NODE_Y;
			const isLastNode = !outgoingEnvironmentIds.has(env.id);
			nodes.push({
				id: env.id,
				type: 'environment',
				position: { x, y },
				data: {
					environment: env,
					onEdit: handleEditEnvironment,
					onDelete: handleDeleteEnvironment,
					components,
					contextSubdomains,
					clients,
					apiKeys,
					onAddEnvironmentComponent: handleAddEnvironmentComponent,
					onRemoveEnvironmentComponent: handleRemoveEnvironmentComponent,
					onDeleteComponent: handleDeleteComponent,
					onAddEnvironmentClient: handleAddEnvironmentClient,
					onRemoveEnvironmentClient: handleRemoveEnvironmentClient,
					onAddEnvironmentApiKey: handleAddEnvironmentApiKey,
					onRemoveEnvironmentApiKey: handleRemoveEnvironmentApiKey,
					isFirstNode: !incomingEnvironmentIds.has(env.id),
					isLastNode,
					onAddEnvironment: isLastNode ? () => void handleOpenCreateEnvironment(env.id) : undefined,
					onViewReleases: handleViewReleases,
					onViewDetails: handleViewEnvironmentDetails,
					onViewComponentDetails: handleViewComponentDetails,
					onViewClientDetails: handleViewClientNodeDetails,
					onViewApiKeyDetails: handleViewApiKeyNodeDetails,
					onViewEmbeds: handleViewEmbeds,
					isSelectedForReleasePlans: selectedEnvironmentForPlans?.id === env.id,
					isSelectedForDetails: selectedEnvironmentForDetails?.id === env.id,
					selectedComponentForDetails: selectedComponentForDetails,
					selectedClientId,
					selectedApiKeyId,
					visibleLayers: graphLayers,
					onVisibleLayersChange: handleGraphLayersChange,
				},
				draggable: true,
			});
		});

		return { flowNodes: nodes, flowEdges: edges };
	}, [
		environmentsData,
		componentsData?.items,
		contextSubdomains,
		clientsData,
		apiKeysData,
		selectedEnvironmentForPlans,
		selectedEnvironmentForDetails,
		selectedComponentForDetails,
		selectedClientId,
		selectedApiKeyId,
		graphLayers,
		handleEditEnvironment,
		handleDeleteEnvironment,
		handleAddEnvironmentComponent,
		handleRemoveEnvironmentComponent,
		handleDeleteComponent,
		handleAddEnvironmentClient,
		handleRemoveEnvironmentClient,
		handleAddEnvironmentApiKey,
		handleRemoveEnvironmentApiKey,
		handlePromote,
		handleViewReleases,
		handleViewEnvironmentDetails,
		handleViewComponentDetails,
		handleViewClientNodeDetails,
		handleViewApiKeyNodeDetails,
		handleViewEmbeds,
		handleGraphLayersChange,
		handleOpenCreateEnvironment,
		theme.palette.primary.main,
	]);

	// Update nodes and edges when data changes
	React.useEffect(() => {
		setNodes(currentNodes => preserveNodeMeasurements(flowNodes, currentNodes));
		setEdges(flowEdges);
	}, [flowNodes, flowEdges, setNodes, setEdges]);

	const handleRefresh = useCallback(() => {
		refetch();
		toastService.info('Refreshing environments...');
	}, [refetch, toastService]);

	const handleCreateSuccess = useCallback(
		async (result: CreateEnvironmentCommandResult) => {
			// Refetch to get the latest data including the new environment
			let refreshedEnvironments = (await refetch()).data;

			if (createAfterEnvironmentId && result.id) {
				const predecessor = refreshedEnvironments?.find((environment) => environment.id === createAfterEnvironmentId);
				if (predecessor) {
					const transitions = (predecessor.transitions ?? []).map(toEnvironmentTransition);
					if (!transitions.some((transition) => transition.to === result.id)) {
						const nextOrder = transitions.length === 0 ? 0 : Math.max(...transitions.map((transition) => transition.order)) + 1;
						try {
							await client.update(predecessor.id, {
								metadata: {
									...(predecessor.metadata ?? {}),
									[ENVIRONMENT_GRAPH_INITIALIZED_METADATA_KEY]: true,
								},
								transitions: [...transitions, makeEnvironmentTransition(predecessor.id, result.id, nextOrder)],
							});
							refreshedEnvironments = (await refetch()).data;
						} catch (error) {
							console.error('Error linking the new environment to its predecessor:', error);
							toastService.error('Environment created, but its predecessor could not be linked');
						}
					}
				}
			}
			toastService.success('Environment created successfully!');

			// Open release plan details if a release plan was created
			if (result && result.releasePlanId && result.id) {
				closePanelFamilies('releasePlans');
				// Get the updated data after refetch
				const newEnvironment = refreshedEnvironments?.find((env) => env.id === result.id);

				if (newEnvironment) {
					// First, set the selected environment and open the Release Plans panel
					setSelectedEnvironmentId(newEnvironment.id);
					// Then select the release plan
					setSelectedReleasePlan(result.releasePlanId);
					// Finally, open the release plan details panel
					setShowReleasePlanDetails(true);
					// Increment refresh trigger to ensure ReleasePlansPanel shows the new plan
					setReleasePlansRefreshTrigger((prev) => prev + 1);
				}
			}
		},
		[client, closePanelFamilies, createAfterEnvironmentId, refetch, toastService],
	);

	const handleEditSuccess = useCallback(() => {
		refetch();
		toastService.success('Environment updated successfully!');
	}, [refetch, toastService]);

	const hasOpenPanel = activePanelFamily !== null;
	const presenceEnvironmentId = activePanelFamily === 'environmentComponent' ? selectedComponentForDetails?.environment?.id
		: activePanelFamily === 'clients' ? (showClientDetails ? selectedEnvironmentForClientDetails?.id : undefined)
		: activePanelFamily === 'apiKeys' ? (showApiKeyDetails ? selectedEnvironmentForApiKeyDetails?.id : undefined)
		: activePanelFamily === 'embeds' ? embedSelection.environment?.id
		: activePanelFamily === 'environment' || activePanelFamily === 'releasePlans' ? selectedEnvironmentId
		: !activePanelFamily ? nodes.find((node) => node.selected && node.type === 'environment')?.id : undefined;
	const presenceItemId = activePanelFamily === 'environmentComponent' ? selectedComponentForDetails?.component?.id
		: activePanelFamily === 'releasePlans' && showReleasePlanDetails ? selectedReleasePlan
		: activePanelFamily === 'containerImages' && showContainerImageDetails ? selectedContainerImageId
		: activePanelFamily === 'components' && showComponentPanelDetails ? selectedComponentId
		: activePanelFamily === 'clients' && showClientDetails ? selectedClientId
		: activePanelFamily === 'apiKeys' && showApiKeyDetails ? selectedApiKeyId
		: activePanelFamily === 'embeds' ? embedSelection.deploymentId ?? embedSelection.definitionId : undefined;
	const presenceLocation = getEnvironmentPresenceLocation({ family: activePanelFamily, environmentId: presenceEnvironmentId, itemId: presenceItemId });
	const environmentViewers = usePresence('environments', presenceEnvironmentId ?? 'overview', 'overview', presenceLocation);

	if (isLoading || componentsLoading || clientsLoading || apiKeysLoading) {
		return <Spinner />;
	}

	return (
		<PresenceContext.Provider value={environmentViewers}>
		<Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
			<ServiceConnectionMetadataProvider>
				<ReactFlowProvider>
					<ViewTitleBar title="Environments"
						titleAdornment={<PresenceIndicator viewers={environmentViewers} label="Also viewing" showLabel />}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%', overflowX: 'auto' }}>
							<Typography variant="caption" sx={{ flexShrink: 0, ml: 1, color: 'text.secondary' }}>Change</Typography>
							<ButtonGroup variant="outlined" aria-label="Environment actions" sx={{ flexShrink: 0 }}>
								<Button aria-label="Create environment" onClick={() => void handleOpenCreateEnvironment()} title="Create environment without a predecessor">
									<AddCircle />
								</Button>
								<Button aria-label="Refresh environments" onClick={handleRefresh} title="Refresh environments">
									<Refresh />
								</Button>
								<Button aria-label="View releases across all environments" aria-pressed={activePanelFamily === "releasePlans"} variant={activePanelFamily === "releasePlans" ? 'contained' : 'outlined'} onClick={handleViewAllReleases} title="View releases across all environments">
									<RocketLaunch />
								</Button>
							</ButtonGroup>
							<Typography variant="caption" sx={{ flexShrink: 0, ml: 1, color: 'text.secondary' }}>Artifacts</Typography>
							<ButtonGroup variant="outlined" aria-label="Deployment resources" sx={{ flexShrink: 0 }}>
								<Button aria-label="View container images" aria-pressed={activePanelFamily === "containerImages"} variant={activePanelFamily === "containerImages" ? 'contained' : 'outlined'} onClick={handleOpenContainerImagesPanel} title="View container images">
									<Inventory2 />
								</Button>
								<Button aria-label="View components" aria-pressed={activePanelFamily === "components"} variant={activePanelFamily === "components" ? 'contained' : 'outlined'} onClick={handleOpenComponentsPanel} title="View components">
									<Extension />
								</Button>
							</ButtonGroup>
							<Typography variant="caption" sx={{ flexShrink: 0, ml: 1, color: 'text.secondary' }}>Access</Typography>
							<ButtonGroup variant="outlined" aria-label="Clients and keys" sx={{ flexShrink: 0 }}>
								<Button aria-label="View API clients" aria-pressed={activePanelFamily === "clients"} variant={activePanelFamily === "clients" ? 'contained' : 'outlined'} onClick={handleOpenClientsPanel} title="View API clients">
									<Groups />
								</Button>
								<Button aria-label="View API keys" aria-pressed={activePanelFamily === "apiKeys"} variant={activePanelFamily === "apiKeys" ? 'contained' : 'outlined'} onClick={handleOpenApiKeysPanel} title="View API keys">
									<VpnKey />
								</Button>
								<Button aria-label="View App Embeds" aria-pressed={activePanelFamily === "embeds"} variant={activePanelFamily === "embeds" ? 'contained' : 'outlined'} onClick={() => handleViewEmbeds()} title="View App Embeds">
									<Code />
								</Button>
							</ButtonGroup>
						</Box>
					</ViewTitleBar>
					<Box sx={{ display: 'flex', gap: 2, px: 2, py: presenceLocation && viewersForItem(environmentViewers, presenceLocation.itemType, presenceLocation.itemId).length ? 0.75 : 0, flexWrap: 'wrap' }}>
						{presenceLocation && <PresenceIndicator viewers={viewersForItem(environmentViewers, presenceLocation.itemType, presenceLocation.itemId)} label="Viewing the same selection" showLabel />}
					</Box>
					<Box sx={(theme) => ({ flexGrow: 1, minHeight: 0, width: '100%', px: `${theme.layout.edgeGap}px`, pb: `${theme.layout.edgeGap}px` })}>
						<Paper sx={{ height: '100%', minHeight: 400, overflow: 'hidden' }}>
							<EnvironmentsFlowGraph
								nodes={nodes}
								edges={edges}
								onNodesChange={onNodesChange}
								onEdgesChange={onEdgesChange}
								onConnect={handleEnvironmentConnect}
								onEdgesDelete={handleEnvironmentEdgesDelete}
								onNodeDragStop={handleEnvironmentNodeDragStop}
								isValidConnection={isValidEnvironmentConnection}
							/>
						</Paper>
					</Box>{' '}
					<CreateEnvironmentDialog
						open={openCreateDialog}
						setOpen={handleCreateDialogOpenChange}
						onSuccess={handleCreateSuccess}
						defaultOrder={nextEnvironmentOrder}
						defaultCloudProvider={defaultCreateCloudProvider}
						onCloudProviderChange={setLastSelectedCloudProvider}
						title={createAfterEnvironmentId ? 'Create Environment After Current' : 'Create Root Environment'}
						initialMetadata={createEnvironmentMetadata}
					/>
					<EditEnvironmentDialog open={openEditDialog} setOpen={setOpenEditDialog} environment={selectedEnvironment} onSuccess={handleEditSuccess} />
					<ConfirmDialog
						open={openDeleteDialog}
						title="Delete Environment"
						message={`Are you sure you want to delete the environment "${environmentToDelete?.name}"? This action cannot be undone.`}
						confirmText="Delete"
						confirmColor="error"
						onConfirm={handleConfirmDelete}
						onCancel={handleCancelDelete}
					/>
					<ConfirmDialog
						open={!!pendingClientOperation}
						title={
							pendingClientOperation?.operation === 'removeClient'
								? 'Remove Client'
								: pendingClientOperation?.operation === 'rotateClientSecret'
									? 'Rotate Client Secret'
									: pendingClientOperation?.operation === 'redeployClient'
										? 'Redeploy Client'
										: 'Deploy Client'
						}
						message={
							pendingClientOperation
								? pendingClientOperation.operation === 'rotateClientSecret'
									? `Rotate the secret for "${pendingClientOperation.client.name}" in ${pendingClientOperation.environment.name}? This will queue a release plan.`
									: `${pendingClientOperation.operation === 'removeClient' ? 'Remove' : pendingClientOperation.operation === 'redeployClient' ? 'Redeploy' : 'Deploy'} "${pendingClientOperation.client.name}" ${pendingClientOperation.operation === 'removeClient' ? 'from' : 'to'} ${pendingClientOperation.environment.name}? This will queue a release plan.`
								: ''
						}
						confirmText={
							pendingClientOperation?.operation === 'removeClient'
								? 'Remove'
								: pendingClientOperation?.operation === 'rotateClientSecret'
									? 'Rotate'
									: pendingClientOperation?.operation === 'redeployClient'
										? 'Redeploy'
										: 'Deploy'
						}
						confirmColor={pendingClientOperation?.operation === 'removeClient' ? 'error' : 'primary'}
						onConfirm={handleConfirmClientOperation}
						onCancel={() => setPendingClientOperation(null)}
					/>
					<ConfirmDialog
						open={!!pendingApiKeyOperation}
						title={
							pendingApiKeyOperation?.operation === 'removeApiKey'
								? 'Remove API Key'
								: pendingApiKeyOperation?.operation === 'rotateApiKeySecret'
									? 'Rotate API Key'
									: pendingApiKeyOperation?.operation === 'redeployApiKey'
										? 'Redeploy API Key'
										: 'Deploy API Key'
						}
						message={
							pendingApiKeyOperation
								? pendingApiKeyOperation.operation === 'rotateApiKeySecret'
									? `Rotate the key value for "${pendingApiKeyOperation.apiKey.name}" in ${pendingApiKeyOperation.environment.name}? This will queue a release plan.`
									: `${pendingApiKeyOperation.operation === 'removeApiKey' ? 'Remove' : pendingApiKeyOperation.operation === 'redeployApiKey' ? 'Redeploy' : 'Deploy'} "${pendingApiKeyOperation.apiKey.name}" ${pendingApiKeyOperation.operation === 'removeApiKey' ? 'from' : 'to'} ${pendingApiKeyOperation.environment.name}? This will queue a release plan.`
								: ''
						}
						confirmText={
							pendingApiKeyOperation?.operation === 'removeApiKey'
								? 'Remove'
								: pendingApiKeyOperation?.operation === 'rotateApiKeySecret'
									? 'Rotate'
									: pendingApiKeyOperation?.operation === 'redeployApiKey'
										? 'Redeploy'
										: 'Deploy'
						}
						confirmColor={pendingApiKeyOperation?.operation === 'removeApiKey' ? 'error' : 'primary'}
						onConfirm={handleConfirmApiKeyOperation}
						onCancel={() => setPendingApiKeyOperation(null)}
					/>
					{/* Unified Dialog for Promotion and Component Operations */}
					{openPromoteDialog && promoteData && promoteData.sourceEnv && promoteData.targetEnv && (
						<PromoteDialog
							open={openPromoteDialog}
							onClose={handlePromoteClose}
							operation="promote"
							sourceEnvironment={promoteData.sourceEnv}
							environment={promoteData.targetEnv}
							onSuccess={handlePromoteSuccess}
						/>
					)}
					{openPromoteDialog && componentOperationData && (
						<PromoteDialog
							open={openPromoteDialog}
							onClose={handlePromoteClose}
							operation={componentOperationData.operation}
							environment={componentOperationData.environment}
							component={componentOperationData.component}
							currentVersion={componentOperationData.currentVersion}
							onSuccess={handlePromoteSuccess}
						/>
					)}
					<ResponsivePanelContainer
						isMobile={isMobile}
						open={hasOpenPanel}
						onClose={() => closePanelFamilies()}
						ariaLabel={activePanelFamily ? `${activePanelFamily} panel` : undefined}
					>
						{/* Release Plans Panel */}
						{activePanelFamily === 'releasePlans' && (
							<ReleasePlansPanel
								environment={selectedEnvironmentForPlans ?? undefined}
								environments={environmentsData ?? []}
								onClose={handleCloseReleasePlansPanel}
								onReleasePlanSelect={handleReleasePlanSelect}
								onReleasePlanDelete={handleReleasePlanDelete}
								selectedReleasePlanId={selectedReleasePlan}
								refreshTrigger={releasePlansRefreshTrigger}
								onStatusChange={handleReleasePlanStatusChange}
								onCollapseChange={setReleasePlansPanelCollapsed}
								width={releasePlansPanelWidth}
								setWidth={setReleasePlansPanelWidth}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
						{/* Release Plan Details Panel */}
						{showReleasePlanDetails && selectedReleasePlan && (
							<ReleasePlanDetailsPanel
								releasePlanId={selectedReleasePlan}
								onClose={handleCloseReleasePlanDetails}
								onStatusChange={handleReleasePlanStatusChange}
								listPanelCollapsed={releasePlansPanelCollapsed}
								rightOffset={releasePlansPanelWidth}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
						{/* Environment Details Panel */}
						{activePanelFamily === 'environment' && selectedEnvironmentForDetails && (
							<EnvironmentDetailsPanel
								environment={selectedEnvironmentForDetails}
								onClose={handleCloseEnvironmentDetails}
								onRefresh={refreshData}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
						{/* Environment Component Details Panel */}
						{activePanelFamily === 'environmentComponent' && selectedComponentForDetails && (
							<EnvironmentComponentDetailsPanel
								component={selectedComponentForDetails.component}
								environmentComponent={selectedComponentForDetails.environmentComponent}
								environment={selectedComponentForDetails.environment}
								clients={clientsData}
								apiKeys={apiKeysData}
								onClose={handleCloseComponentDetails}
								onRefresh={refreshComponentDetails}
								onClientSelect={handleViewClientNodeDetails}
								onApiKeySelect={handleViewApiKeyNodeDetails}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
						{/* Container Images Panel */}
						{activePanelFamily === 'containerImages' && (
							<ContainerImagesPanel
								onClose={handleCloseContainerImagesPanel}
								onImageSelect={handleContainerImageSelect}
								selectedImageId={selectedContainerImageId}
								refreshTrigger={containerImagesRefreshTrigger}
								onCollapseChange={setContainerImagesPanelCollapsed}
								width={containerImagesPanelWidth}
								setWidth={setContainerImagesPanelWidth}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
						{/* Container Image Details Panel */}
						{showContainerImageDetails && selectedContainerImageId && (
							<ContainerImageDetailsPanel
								imageId={selectedContainerImageId}
								onClose={handleCloseContainerImageDetails}
								onVersionAdded={handleContainerImageVersionAdded}
								listPanelCollapsed={containerImagesPanelCollapsed}
								rightOffset={containerImagesPanelWidth}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
						{/* Components Panel */}
						{activePanelFamily === 'components' && (
							<ComponentsPanel
								onClose={handleCloseComponentsPanel}
								onComponentSelect={handleComponentSelect}
								selectedComponentId={selectedComponentId}
								components={componentsData?.items}
								isLoading={componentsLoading}
								onRefresh={refreshData}
								onCollapseChange={setComponentsPanelCollapsed}
								width={componentsPanelWidth}
								setWidth={setComponentsPanelWidth}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
						{/* Clients Panel */}
						{activePanelFamily === 'clients' && (
							<ClientsPanel
								onClose={handleCloseClientsPanel}
								onClientSelect={handleClientSelect}
								selectedClientId={selectedClientId}
								onCollapseChange={setClientsPanelCollapsed}
								width={clientsPanelWidth}
								setWidth={setClientsPanelWidth}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
                        {activePanelFamily === 'embeds' && <EmbedsPanel
                            key={(embedSelection.environment?.id ?? '') + (embedSelection.definitionId ?? '') + (embedSelection.deploymentId ?? '') + (embedSelection.action ?? '')}
                            environments={environmentsData ?? []} initialEnvironment={embedSelection.environment}
                            initialDefinitionId={embedSelection.definitionId} initialDeploymentId={embedSelection.deploymentId} initialAction={embedSelection.action}
                            onClose={() => closePanelFamilies()} top={ENVIRONMENT_PANELS_TOP}
                            onReleaseCreated={(id, environment) => void handlePromoteSuccess(id, environment)} />}
						{/* API Keys Panel */}
						{activePanelFamily === 'apiKeys' && (
							<ApiKeysPanel
								onClose={handleCloseApiKeysPanel}
								onApiKeySelect={handleApiKeySelect}
								selectedApiKeyId={selectedApiKeyId}
								onCollapseChange={setApiKeysPanelCollapsed}
								width={apiKeysPanelWidth}
								setWidth={setApiKeysPanelWidth}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
						{/* Component Details Panel */}
						{showComponentPanelDetails && selectedComponentId && (
							<ComponentDetailsPanel
								componentId={selectedComponentId}
								components={componentsData?.items}
								clients={clientsData}
								apiKeys={apiKeysData}
								onClose={handleCloseComponentPanelDetails}
								onRefresh={refreshData}
								onClientSelect={handleClientSelect}
								onApiKeySelect={handleApiKeySelect}
								listPanelCollapsed={componentsPanelCollapsed}
								rightOffset={componentsPanelWidth}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
						{/* Client Details Panel */}
						{showClientDetails && selectedClientId && (
							<ClientDetailsPanel
								clientId={selectedClientId}
								environments={environmentsData ?? []}
								onRotateEnvironmentSecret={handleRotateEnvironmentClientSecret}
								environmentId={selectedEnvironmentForClientDetails?.id}
								environment={selectedEnvironmentForClientDetails}
								environmentClient={selectedEnvironmentClientForDetails}
								components={componentsData?.items}
								onClose={handleCloseClientDetails}
								onRefresh={refreshData}
								onRotateSecret={
									selectedEnvironmentForClientDetails && selectedClientForDetails
										? () => handleRotateEnvironmentClientSecret(selectedEnvironmentForClientDetails, selectedClientForDetails)
										: undefined
								}
								listPanelCollapsed={clientsPanelCollapsed}
								rightOffset={clientsPanelWidth}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
						{/* API Key Details Panel */}
						{showApiKeyDetails && selectedApiKeyId && (
							<ApiKeyDetailsPanel
								apiKeyId={selectedApiKeyId}
								environments={environmentsData ?? []}
								onRotateEnvironmentSecret={handleRotateEnvironmentApiKeySecret}
								environment={selectedEnvironmentForApiKeyDetails}
								environmentApiKey={selectedEnvironmentApiKeyForDetails}
								components={componentsData?.items}
								onClose={handleCloseApiKeyDetails}
								onRefresh={refreshData}
								onRotateSecret={
									selectedEnvironmentForApiKeyDetails && selectedApiKeyForDetails
										? () => handleRotateEnvironmentApiKeySecret(selectedEnvironmentForApiKeyDetails, selectedApiKeyForDetails)
										: undefined
								}
								listPanelCollapsed={apiKeysPanelCollapsed}
								rightOffset={apiKeysPanelWidth}
								top={ENVIRONMENT_PANELS_TOP}
							/>
						)}
					</ResponsivePanelContainer>
				</ReactFlowProvider>
			</ServiceConnectionMetadataProvider>
		</Box>
		</PresenceContext.Provider>
	);
}
